<template>
    <div class="vulnerability-evidence">
        <div class="summary-top-bar">
            <div class="left">
                Vulnerability Evidence
                <span class="beta-pill">Beta</span>
            </div>
            <div class="right">
                <span class="meta">Consumer Duty &rarr; Consumer Vulnerability</span>
                <span class="meta" v-if="syncedAtDisplay"> &middot; CRM data synced {{ syncedAtDisplay }}</span>
            </div>
            <div style="clear:both"></div>
        </div>

<!--        <div class="mock-banner">-->
<!--            <i class="fa fa-flask"></i>-->
<!--            Beta build. Any card, indicator or section below marked <span class="illustrative-tag">Illustrative</span>-->
<!--            is not yet connected to real data. Everything else — Control Score, Nature &amp; Scale / Identification /-->
<!--            Support (once Intelliflo has synced), Operational Evidence Score, Control Effectiveness, Client Notes-->
<!--            (CRM) and the MO&reg; Regulatory Intelligence overview (once the per-question AI assessment has run) — is real.-->
<!--        </div>-->

        <!-- Refresh progress -->
        <div v-if="showProgress" class="refresh-progress" :class="'refresh-progress-' + progressTone">
            <div class="refresh-progress-head">
                <strong v-if="refreshRunning"><i class="fa fa-spinner fa-spin"></i> Updating Vulnerability Evidence</strong>
                <strong v-else-if="progress.stalled"><i class="fa fa-triangle-exclamation"></i> The update has stopped responding</strong>
                <strong v-else-if="progress.stage === 'failed'"><i class="fa fa-triangle-exclamation"></i> The update did not finish</strong>
                <strong v-else><i class="fa fa-check"></i> Update finished</strong>
                <span class="refresh-progress-time" v-if="progressElapsed">{{ progressElapsed }}</span>
            </div>
            <ol class="refresh-steps">
                <li v-for="step in progressSteps" :key="step.key" :class="'step-' + step.state">
                    <span class="step-marker">
                        <i v-if="step.state === 'done'" class="fa fa-check"></i>
                        <i v-else-if="step.state === 'current'" class="fa fa-spinner fa-spin"></i>
                        <i v-else-if="step.state === 'failed'" class="fa fa-xmark"></i>
                    </span>
                    {{ step.label }}
                </li>
            </ol>
            <p v-if="progress.message" class="refresh-progress-message">{{ progress.message }}</p>
            <p v-if="progress.stalled" class="refresh-progress-message">No progress for over {{ staleMinutes }} minutes. You can start the update again.</p>
            <p v-if="!refreshRunning && progress.stage === 'done'" class="refresh-progress-message">
                <a href="#" @click.prevent="reloadPage">Reload the page</a> to see the updated ratings and summary.
            </p>
        </div>

        <!-- 5 top-level indicators -->
        <div class="kpi-row">
            <div class="kpi-card" v-for="kpi in kpis" :key="kpi.label">
                <div class="kpi-value" :style="{ color: kpi.color }">{{ kpi.display }}</div>
                <div class="kpi-label">
                    {{ kpi.label }}
                    <span v-if="kpi.formula" class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">{{ kpi.formula }}</span></span>
                    <span v-if="!kpi.live" class="illustrative-tag">Illustrative</span>
                </div>
            </div>
        </div>

        <!-- Three-tier RAG summary -->
        <div class="traffic-row">
            <div class="traffic-item" v-for="t in trafficLights" :key="t.label">
                <span class="dot" :style="{ backgroundColor: t.color }"></span>
                <div>
                    <div class="traffic-value">{{ t.value }}</div>
                    <div class="traffic-label">
                        {{ t.label }}
                        <span v-if="t.formula" class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">{{ t.formula }}</span></span>
                        <span v-if="!t.live" class="illustrative-tag">Illustrative</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- MO® Regulatory Intelligence -->
        <div class="ai-narrative" id="mo-regulatory-intelligence">
            <div class="ai-header">
                <img src="/images/mo-chat.png" alt="" class="ai-logo"> MO&reg; Regulatory Intelligence
                <span class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">An AI-written overall assessment of this business function. It sets your declared audit answers against the evidence from Action Tracker (notes and files), MO&reg;nitor document scans and your Intelliflo CRM data. The rating is {{ ragFormula }}</span></span>
                <span class="rag-pill" :class="overallRagClass">{{ overallRagLabel }}</span>
            </div>
            <template v-if="intelligence">
                <div class="ai-headline">Consumer Vulnerability &ndash; {{ overallRagLabel }}</div>
                <p>{{ intelligence.summary }}</p>
                <div v-for="s in intelligenceSections" :key="s.key" class="ai-section">
                    <div class="ai-section-title">{{ s.title }}</div>
                    <p>{{ s.text }}</p>
                </div>
                <div v-if="intelligence.priorities.length" class="ai-priority">
                    <strong>{{ intelligence.priorities.length > 1 ? 'Priorities' : 'Priority' }}:</strong>
                    <ol v-if="intelligence.priorities.length > 1">
                        <li v-for="(p, idx) in intelligence.priorities" :key="idx">{{ p }}</li>
                    </ol>
                    <template v-else> {{ intelligence.priorities[0] }}</template>
                </div>
                <div v-if="intelligence.prompt" class="ai-prompt">
                    <a href="#" @click.prevent="showPrompt = !showPrompt">
                        <i class="fa" :class="showPrompt ? 'fa-minus' : 'fa-plus'"></i>
                        {{ showPrompt ? 'Hide' : 'View' }} the prompt and data behind this summary
                    </a>
                    <pre v-if="showPrompt" class="ai-prompt-text">{{ intelligence.prompt }}</pre>
                </div>
            </template>
            <p v-else-if="hasEvidenceRollup">{{ parsedEvidenceRollup.narrative }}</p>
            <p v-else class="section-status-message">Not yet assessed — this appears once Intelliflo has synced and the AI assessment has run.</p>
            <div class="ai-footnote">
                <template v-if="intelligence">AI-generated overview of your audit answers, Action Tracker evidence and Intelliflo CRM data &middot; generated {{ intelligence.generated_at || 'at the last analysis' }}<template v-if="!intelligenceSections.length"> &middot; the section-by-section summary appears after the next analysis (use Re-analyze below)</template></template>
                <template v-else-if="hasEvidenceRollup">Summary of {{ parsedQuestionVerdicts.length }} question ratings &middot; the AI-written overview appears after the next analysis (use Re-analyze below)</template>
            </div>
        </div>

        <!-- Drill-down -->
        <div class="drilldown">
            <div class="drilldown-section" v-for="section in sections" :key="section.key">
                <div class="section-header" @click="toggle(section.key)">
                    <i class="fa" :class="openSections[section.key] ? 'fa-minus' : 'fa-plus'"></i>
                    {{ section.title }}
                    <span v-if="section.info" class="info-icon section-info" tabindex="0" @click.stop>&#9432;<span class="info-tooltip">{{ section.info }}</span></span>
                    <span v-if="!section.live" class="illustrative-tag">Illustrative</span>
                </div>
                <div class="section-body" v-show="openSections[section.key]">
                    <template v-if="section.type === 'verdicts'">
                        <div class="verdicts-toolbar" v-if="refreshUrl">
                            <div class="changes-banner" v-if="parsedDataChanges.length">
                                <span>
                                    <strong>Evidence changed since the last analysis:</strong>
                                    {{ parsedDataChanges.join(', ') }}. Re-analyze to update the ratings below.
                                </span>
                                <button type="button" class="refresh-btn refresh-btn-primary" :disabled="refreshBusy" @click="refreshEvidence('assess')">
                                    <i class="fa fa-rotate"></i> Re-analyze
                                </button>
                            </div>
                            <template v-else>
                                <span class="assessed-at" v-if="assessedAt">Last assessed {{ assessedAt }}</span>
                                <button type="button" class="refresh-btn" :disabled="refreshBusy" @click="refreshEvidence('full')"
                                        title="Re-pull this firm's vulnerability and service-case data from Intelliflo, then re-analyze. Use it after changing records in Intelliflo.">
                                    <i class="fa fa-rotate"></i> Refresh from Intelliflo
                                </button>
                            </template>
                            <span class="refresh-message" v-if="refreshRunning">
                                <i class="fa fa-spinner fa-spin"></i> {{ progress.label }}&hellip; (progress is shown at the top of this panel)
                            </span>
                            <span class="refresh-message refresh-error" v-if="refreshState === 'error'">Couldn’t start the analysis — please try again.</span>
                        </div>
                        <p v-if="section.questions.length === 0" class="section-status-message">{{ section.statusMessage }}</p>
                        <table v-else class="table verdicts-table">
                            <thead>
                                <tr>
                                    <th>Question</th>
                                    <th>Firm's answer</th>
                                    <th>AI verdict</th>
                                    <th v-if="showQuestionRatings">
                                        Ratings
                                        <span class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">Three levels for each question. Control: how strongly the firm declared it has this in place (from its own answer). Evidence: what the Action Tracker, MO&reg;nitor and Intelliflo evidence supports. Outcome: how vulnerable clients actually fare, shown only for questions whose evidence includes outcomes — it is the business-function outcome score, based on service cases. Banded &lt;34% Red, 34–67% Amber, 68%+ Green.</span></span>
                                    </th>
                                    <th>Rationale</th>
                                    <th>Evidence</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="q in section.questions" :key="q.question_id">
                                    <td class="verdict-question">{{ q.question_text }}</td>
                                    <td class="verdict-answer">{{ q.firm_answer }}</td>
                                    <td>
                                        <span class="classification-badge" :class="verdictBadgeClass(q.verdict)">{{ verdictLabel(q.verdict) }}</span>
                                        <span class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">AI-assessed by comparing the firm's declared answer for this question against the CRM metrics relevant to it, per this question's regulatory mapping rule. See the rationale column for the specific reasoning.</span></span>
                                    </td>
                                    <td v-if="showQuestionRatings" class="verdict-ratings">
                                        <div v-for="r in ratingLines(q)" :key="r.label" class="rating-line" :title="r.title">
                                            <span class="rag-dot" :class="r.cls"></span>
                                            <span class="rating-label">{{ r.label }}</span>
                                            <span class="rating-value">{{ r.text }}</span>
                                        </div>
                                    </td>
                                    <td class="verdict-rationale">{{ q.rationale }}</td>
                                    <td class="verdict-evidence">
                                        <template v-if="evidenceChips(q).length">
                                            <template v-for="chip in evidenceChips(q)" :key="chip.label">
                                                <a v-if="chip.href" :href="chip.href" target="_blank" rel="noopener" class="evidence-chip evidence-chip-link" :title="chip.title">
                                                    {{ chip.label }}<span v-if="chip.sub" class="evidence-chip-sub">{{ chip.sub }}</span><span v-if="chip.note" class="evidence-chip-sub evidence-chip-note">{{ chip.note }}</span>
                                                </a>
                                                <span v-else-if="chip.expandable" class="evidence-chip evidence-chip-toggle" role="button" tabindex="0" :title="chip.title"
                                                      @click="toggleCrm(q)" @keydown.enter="toggleCrm(q)">{{ chip.label }} {{ openCrm[q.question_id] ? '−' : '+' }}</span>
                                                <span v-else class="evidence-chip" :title="chip.title">{{ chip.label }}</span>
                                            </template>
                                            <div v-if="openCrm[q.question_id] && q.evidence && q.evidence.crm_details" class="crm-detail">
                                                <div v-for="line in q.evidence.crm_details" :key="line.label" class="crm-detail-row">
                                                    <span class="crm-detail-label">{{ line.label }}</span>
                                                    <span class="crm-detail-value">{{ line.value }}</span>
                                                </div>
                                                <a href="#" class="crm-detail-link" @click.prevent="showClientNotes">See the actual notes in Client Notes (CRM)</a>
                                            </div>
                                        </template>
                                        <span v-else class="evidence-none">No evidence yet</span>
                                        <a v-if="q.action_tracker_url" :href="q.action_tracker_url" class="evidence-link">Add / view in Action Tracker &rarr;</a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                    <template v-else-if="section.type === 'notes'">
                        <p v-if="section.notes.length === 0" class="section-status-message">{{ section.statusMessage }}</p>
                        <table v-else class="table notes-table">
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Source</th>
                                    <th>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(n, idx) in section.notes" :key="idx">
                                    <td class="note-client">{{ n.client_name }}</td>
                                    <td class="note-source">{{ n.source }}</td>
                                    <td class="note-text">{{ n.text }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                    <template v-else>
                        <p v-if="section.metrics.length === 0" class="section-status-message">{{ syncStatusMessage }}</p>
                        <table v-else class="table metrics-table">
                            <tbody>
                                <tr v-for="m in section.metrics" :key="m.label">
                                    <td class="metric-label">
                                        {{ m.label }}
                                        <span v-if="m.formula" class="info-icon" tabindex="0">&#9432;<span class="info-tooltip">{{ m.formula }}</span></span>
                                    </td>
                                    <td class="metric-value">{{ m.value }}</td>
                                    <td class="metric-bar">
                                        <div class="bar-track" v-if="m.pct !== null">
                                            <div class="bar-fill" :style="barStyle(m)"></div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </div>
            </div>
        </div>

        <!-- Regulatory exceptions -->
        <div class="exceptions-panel">
            <div class="section-header static">
                <i class="fa fa-triangle-exclamation"></i> Regulatory Exceptions ({{ hasExceptions ? parsedExceptions.length : 0 }})
                <span v-if="!hasExceptions" class="illustrative-tag">Illustrative</span>
            </div>
            <p v-if="!hasExceptions" class="section-status-message">{{ syncStatusMessage || 'CRM data has synced, but exceptions haven’t been computed yet.' }}</p>
            <p v-else-if="parsedExceptions.length === 0" class="section-status-message">No regulatory exceptions detected against the current thresholds.</p>
            <table v-else class="table exceptions-table">
                <thead>
                    <tr>
                        <th>Exception</th>
                        <th>Related question</th>
                        <th>Severity</th>
                        <th>Affected clients</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="e in parsedExceptions" :key="e.id">
                        <td>{{ e.title }}</td>
                        <td>{{ e.question || '—' }}</td>
                        <td><span class="severity-badge" :class="e.severity_class">{{ e.severity }}</span></td>
                        <td>{{ e.affected !== null ? e.affected : '—' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="confidence-footnote">
            <template v-if="hasCrmConfidence">
                CRM Data Confidence = Completeness ({{ parsedCrmConfidence.completeness }}%) &times;
                Coverage ({{ parsedCrmConfidence.coverage }}%) &times;
                Recency ({{ parsedCrmConfidence.recency }}%) &times;
                Reliability ({{ parsedCrmConfidence.reliability }}%) = {{ parsedCrmConfidence.score }}%.
                Missing CRM data is not automatically treated as a regulatory failure.
            </template>
            <template v-else>
                CRM Data Confidence = Completeness &times; Coverage &times; Recency &times; Reliability.
                Missing CRM data is not automatically treated as a regulatory failure.
            </template>
        </div>
    </div>
</template>

<script>
const SECTION_TITLES = {
    nature: 'Nature & Scale',
    identification: 'Identification',
    support: 'Support',
    outcomes: 'Outcomes',
    control: 'Control Effectiveness',
};

// What each area covers, from the Consumer Vulnerability regulatory data
// brief — limited to the data points MO can actually get from Intelliflo and
// MO itself (Intelliflo has no complaints, referrals or resolution-time data).
const SECTION_INFO = {
    nature: 'Nature & Scale: vulnerable clients as a share of all clients; vulnerability characteristics (Intelliflo status Yes / Potential, temporary or permanent, and the FCA drivers: health, life events, resilience, capability); how vulnerable clients are spread by age and by adviser; clients flagged only on older service-case fields.',
    identification: 'Identification: vulnerabilities newly recorded in the last 12 months; records with complete data (category, notes and review date); which of those details are missing; records not reviewed in 24+ months.',
    support: 'Support: vulnerable clients with a linked service case; open service cases; needs & adjustments made; "action taken" notes; Power of Attorney; health or medical notes; whether client-portal suitability was assessed.',
    outcomes: 'Outcomes: how vulnerable clients fare compared with other clients, measured by Intelliflo service cases per client (vulnerable vs non-vulnerable cohort). Intelliflo holds no complaints data, so complaint outcomes are not shown.',
    control: 'Control Effectiveness: your MO audit answers set against the actual evidence — Intelliflo CRM data, Action Tracker notes and files, and MO®nitor policy scans — with an AI verdict for each question.',
};

export default {
    name: 'VulnerabilityEvidenceComponent',
    props: {
        controlScore: {
            type: [String, Number],
            default: null,
        },
        liveMetrics: {
            type: String,
            default: '',
        },
        ioConnected: {
            type: String,
            default: '',
        },
        statusUrl: {
            type: String,
            default: '',
        },
        refreshStatus: {
            type: String,
            default: '',
        },
        questionVerdicts: {
            type: String,
            default: '',
        },
        evidenceRollup: {
            type: String,
            default: '',
        },
        exceptions: {
            type: String,
            default: '',
        },
        crmConfidence: {
            type: String,
            default: '',
        },
        crmNotes: {
            type: String,
            default: '',
        },
        refreshUrl: {
            type: String,
            default: '',
        },
        assessedAt: {
            type: String,
            default: '',
        },
        dataChanges: {
            type: String,
            default: '',
        },
        showQuestionRatings: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            openSections: {
                nature: true,
                identification: false,
                support: false,
                outcomes: false,
                control: false,
            },
            refreshState: 'idle',
            progress: null,
            // Set once this page has watched a refresh run, so its result stays
            // on screen; a refresh that finished before the page loaded is
            // already reflected in the figures and is not shown again.
            watchedRefresh: false,
            pollTimer: null,
            now: Date.now(),
            clockTimer: null,
            openCrm: {},
            showPrompt: false,
        };
    },
    mounted() {
        if (this.refreshStatus) {
            try {
                this.progress = JSON.parse(this.refreshStatus) || null;
            } catch (e) {
                this.progress = null;
            }
        }
        if (this.refreshRunning) {
            this.watchedRefresh = true;
            this.startPolling();
        }
    },
    beforeUnmount() {
        this.stopPolling();
    },
    computed: {
        controlScorePercent() {
            if (this.controlScore === null || this.controlScore === '' || this.controlScore === undefined) {
                return null;
            }
            const value = Number(this.controlScore);
            return Number.isNaN(value) ? null : value;
        },
        controlScoreDisplay() {
            return this.controlScorePercent === null ? 'Not yet assessed' : this.controlScorePercent + '%';
        },
        controlScoreColor() {
            return this.ragColor(this.controlScorePercent);
        },
        controlScoreRagLabel() {
            return this.ragLabel(this.controlScorePercent);
        },
        parsedEvidenceRollup() {
            if (!this.evidenceRollup) return null;
            try {
                return JSON.parse(this.evidenceRollup) || null;
            } catch (e) {
                return null;
            }
        },
        hasEvidenceRollup() {
            return this.parsedEvidenceRollup !== null;
        },
        evidenceScoreColor() {
            return this.hasEvidenceRollup ? this.ragColor(this.parsedEvidenceRollup.evidence_score) : '#999';
        },
        evidenceScoreRagLabel() {
            return this.hasEvidenceRollup ? this.ragLabel(this.parsedEvidenceRollup.evidence_score) : 'Not assessed';
        },
        outcomeScorePercent() {
            const value = this.parsedLiveMetrics?.outcome_score;
            return value === null || value === undefined ? null : value;
        },
        parsedExceptions() {
            if (!this.exceptions) return null;
            try {
                return JSON.parse(this.exceptions);
            } catch (e) {
                return null;
            }
        },
        hasExceptions() {
            return this.parsedExceptions !== null;
        },
        parsedCrmConfidence() {
            if (!this.crmConfidence) return null;
            try {
                return JSON.parse(this.crmConfidence);
            } catch (e) {
                return null;
            }
        },
        hasCrmConfidence() {
            return this.parsedCrmConfidence !== null;
        },
        parsedCrmNotes() {
            if (!this.crmNotes) return null;
            try {
                return JSON.parse(this.crmNotes);
            } catch (e) {
                return null;
            }
        },
        hasCrmNotes() {
            return this.parsedCrmNotes !== null;
        },
        parsedDataChanges() {
            if (!this.dataChanges) return [];
            try {
                const parsed = JSON.parse(this.dataChanges);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        },
        refreshRunning() {
            return !!(this.progress && this.progress.running);
        },
        refreshBusy() {
            return this.refreshState === 'sending' || this.refreshRunning;
        },
        staleMinutes() {
            return this.progress?.stale_after_minutes ?? 30;
        },
        showProgress() {
            if (!this.progress) return false;
            if (this.refreshRunning || this.progress.stalled || this.watchedRefresh) return true;
            // A failure stays visible for a day so the firm knows its last update did not go through.
            const finished = Date.parse(this.progress.finished_at || '');
            return this.progress.stage === 'failed' && finished && this.now - finished < 24 * 3600 * 1000;
        },
        progressTone() {
            if (this.refreshRunning) return 'running';
            if (this.progress.stage === 'done' && !this.progress.message) return 'done';
            if (this.progress.stage === 'done') return 'warning';
            return 'failed';
        },
        progressSteps() {
            const p = this.progress;
            const order = ['queued', ...(p.with_crm_sync ? ['crm_sync'] : []), 'assessing', 'summary', 'done'];
            const labels = {
                queued: 'Waiting to start',
                crm_sync: 'Pulling data from Intelliflo',
                assessing: 'AI assessing each question',
                summary: 'Writing the MO® Regulatory Intelligence summary',
                done: 'Finished',
            };
            const failedStep = p.stage === 'failed' || p.stalled;
            // A failure has no step of its own; mark the step it stopped at,
            // which is the furthest step reached before the failure.
            let current = order.indexOf(p.stage);
            if (failedStep && current === -1) current = Math.max(0, order.indexOf(p.failed_stage || 'queued'));

            return order.map((key, idx) => {
                let state = 'pending';
                if (p.stage === 'done' || idx < current) state = 'done';
                else if (idx === current) state = failedStep ? 'failed' : (key === 'done' ? 'done' : 'current');

                return { key, label: labels[key], state };
            });
        },
        progressElapsed() {
            const started = Date.parse(this.progress?.started_at || '');
            if (!started) return '';
            const end = this.refreshRunning ? this.now : (Date.parse(this.progress.finished_at || '') || this.now);
            const seconds = Math.max(0, Math.round((end - started) / 1000));
            const text = seconds < 60 ? seconds + 's' : Math.floor(seconds / 60) + ' min ' + (seconds % 60) + 's';

            return (this.refreshRunning ? 'Running for ' : 'Took ') + text;
        },
        crmConfidenceFormula() {
            const c = this.parsedCrmConfidence;
            if (!c) return '';
            return 'Completeness (' + c.completeness + '%) × Coverage (' + c.coverage + '%) × Recency ('
                + c.recency + '%) × Reliability (' + c.reliability + '%). Reliability is sample-size adequacy '
                + 'of the vulnerable-client count (n/(n+10)) — few vulnerable clients caps this score low even '
                + 'when the other three factors look perfect.';
        },
        outcomeScoreColor() {
            return this.ragColor(this.outcomeScorePercent);
        },
        outcomeScoreRagLabel() {
            return this.ragLabel(this.outcomeScorePercent);
        },
        controlScoreFormula() {
            return 'Sum of scored answers ÷ maximum possible score across all Consumer Vulnerability questions '
                + '(the Score model, recalculated whenever an answer changes).';
        },
        evidenceScoreFormula() {
            const n = this.parsedQuestionVerdicts ? this.parsedQuestionVerdicts.length : 31;
            return `Average of per-question AI verdict scores across all ${n} assessed questions: `
                + 'VALIDATED=100, EMERGING_RISK=60, INSUFFICIENT_EVIDENCE=50, CONTRADICTED=0. '
                + 'The verdicts themselves come from AI; this average is plain arithmetic, not a further AI judgement.';
        },
        outcomeScoreFormula() {
            return '100 − (confidence weight × min(|raw outcome differential|, 100)). Confidence weight = '
                + '(vulnerable clients ÷ (vulnerable clients + 10)) × (comparison service-case events ÷ '
                + '(events + 10)) — shrinks toward 0 when either the vulnerable-client count or the number of '
                + 'observed comparison events is small, so a noisy differential can’t swing the score much.';
        },
        ragBandingNote() {
            return 'Banded from the score: <34% Red, 34–67% Amber, ≥68% Green.';
        },
        kpis() {
            const evidenceKpi = this.hasEvidenceRollup
                ? { label: 'Operational Evidence Score', display: this.parsedEvidenceRollup.evidence_score + '%', color: this.evidenceScoreColor, live: true, formula: this.evidenceScoreFormula }
                : { label: 'Operational Evidence Score', display: 'Not yet assessed', color: '#999', live: false };

            const outcomeKpi = this.outcomeScorePercent !== null
                ? { label: 'Customer Outcome Score', display: Math.round(this.outcomeScorePercent) + '%', color: this.outcomeScoreColor, live: true, formula: this.outcomeScoreFormula }
                : { label: 'Customer Outcome Score', display: 'Not yet assessed', color: '#999', live: false };

            const highCount = this.hasExceptions ? this.parsedExceptions.filter((e) => e.severity === 'High').length : 0;
            const exceptionsKpi = this.hasExceptions
                ? {
                    label: 'Regulatory Exceptions',
                    display: String(this.parsedExceptions.length),
                    color: highCount > 0 ? '#DE301D' : (this.parsedExceptions.length > 0 ? '#F1C40F' : '#2ECC71'),
                    live: true,
                    formula: 'Count of data-quality thresholds crossed (stale/incomplete records, missing service-case '
                        + 'links, elevated outcome differential) plus one row for contradicted answers and one for emerging risks, each listing the questions affected.',
                }
                : { label: 'Regulatory Exceptions', display: 'Not yet assessed', color: '#999', live: false };

            const confidenceKpi = this.hasCrmConfidence
                ? { label: 'CRM Data Confidence', display: this.parsedCrmConfidence.score + '%', color: this.ragColor(this.parsedCrmConfidence.score), live: true, formula: this.crmConfidenceFormula }
                : { label: 'CRM Data Confidence', display: 'Not yet assessed', color: '#999', live: false };

            return [
                { label: 'MO Vulnerability Control Score', display: this.controlScoreDisplay, color: this.controlScoreColor, live: true, formula: this.controlScoreFormula },
                evidenceKpi,
                outcomeKpi,
                exceptionsKpi,
                confidenceKpi,
            ];
        },
        trafficLights() {
            const evidenceTraffic = this.hasEvidenceRollup
                ? { label: 'Operational evidence', value: this.evidenceScoreRagLabel, color: this.evidenceScoreColor, live: true, formula: this.evidenceScoreFormula + ' ' + this.ragBandingNote }
                : { label: 'Operational evidence', value: 'Not assessed', color: '#999', live: false };

            const outcomeTraffic = this.outcomeScorePercent !== null
                ? { label: 'Customer outcomes', value: this.outcomeScoreRagLabel, color: this.outcomeScoreColor, live: true, formula: this.outcomeScoreFormula + ' ' + this.ragBandingNote }
                : { label: 'Customer outcomes', value: 'Not assessed', color: '#999', live: false };

            return [
                { label: 'Declared control', value: this.controlScoreRagLabel, color: this.controlScoreColor, live: true, formula: this.controlScoreFormula + ' ' + this.ragBandingNote },
                evidenceTraffic,
                outcomeTraffic,
            ];
        },
        overallRagLabel() {
            return this.hasEvidenceRollup && this.parsedEvidenceRollup.overall_rag
                ? this.parsedEvidenceRollup.overall_rag
                : 'Not assessed';
        },
        overallRagClass() {
            return 'rag-' + this.overallRagLabel.toLowerCase().replace(/\s+/g, '-');
        },
        ragFormula() {
            const score = this.hasEvidenceRollup ? this.parsedEvidenceRollup.overall_score : null;

            return 'the average of your Declared control, Operational evidence and Customer outcome scores'
                + (score !== null && score !== undefined ? ' (currently ' + score + '%)' : '')
                + ', banded <34% Red, 34–67% Amber, 68%+ Green.';
        },
        intelligence() {
            const i = this.hasEvidenceRollup ? this.parsedEvidenceRollup.intelligence : null;

            return i && i.summary ? { ...i, sections: i.sections || {}, priorities: i.priorities || [] } : null;
        },
        intelligenceSections() {
            if (!this.intelligence) return [];

            return Object.entries(SECTION_TITLES)
                .filter(([key]) => this.intelligence.sections[key])
                .map(([key, title]) => ({ key, title, text: this.intelligence.sections[key] }));
        },
        parsedLiveMetrics() {
            if (!this.liveMetrics) return null;
            try {
                return JSON.parse(this.liveMetrics) || null;
            } catch (e) {
                return null;
            }
        },
        syncedAtDisplay() {
            return this.parsedLiveMetrics?.synced_at ?? null;
        },
        // Whether Intelliflo is connected and CRM data has actually synced — drives
        // whether Nature & Scale / Identification / Support show real numbers or an
        // explicit status message. Never silently falls back to illustrative figures.
        syncState() {
            if (!this.ioConnected) return 'not_connected';
            if (!this.parsedLiveMetrics) return 'pending_sync';
            return 'synced';
        },
        syncStatusMessage() {
            if (this.syncState === 'not_connected') {
                return 'Intelliflo isn’t connected for this account. Connect it under intelliflo office to see live CRM evidence here.';
            }
            if (this.syncState === 'pending_sync') {
                return 'Intelliflo is connected but CRM data hasn’t synced yet. This refreshes automatically the next time Intelliflo data syncs.';
            }
            return '';
        },
        natureSectionMetrics() {
            const m = this.parsedLiveMetrics;
            if (!m) return [];

            const breakdown = m.category_breakdown || {};
            const vulnerablePct = m.active_clients > 0
                ? Math.round((m.vulnerable_clients / m.active_clients) * 1000) / 10
                : 0;

            return [
                { label: 'Active clients', value: String(m.active_clients), pct: null, formula: 'Count of clients synced from Intelliflo for this account (intelligent_office_data).' },
                { label: 'Clients with a recorded vulnerability', value: `${m.vulnerable_clients} (${vulnerablePct}%)`, pct: vulnerablePct, formula: 'Clients with a current Intelliflo Vulnerabilities record ÷ active clients × 100.' },
                { label: 'Health', value: (breakdown.Health ?? 0) + '%', pct: breakdown.Health ?? 0, positive: true, formula: '% of vulnerable clients whose Intelliflo vulnerability record lists this category (a client can have more than one, so the four don’t need to sum to 100%).' },
                { label: 'Life events', value: (breakdown.LifeEvent ?? 0) + '%', pct: breakdown.LifeEvent ?? 0, positive: true, formula: '% of vulnerable clients whose Intelliflo vulnerability record lists this category (a client can have more than one, so the four don’t need to sum to 100%).' },
                { label: 'Resilience', value: (breakdown.Resilience ?? 0) + '%', pct: breakdown.Resilience ?? 0, positive: true, formula: '% of vulnerable clients whose Intelliflo vulnerability record lists this category (a client can have more than one, so the four don’t need to sum to 100%).' },
                { label: 'Capability', value: (breakdown.Capability ?? 0) + '%', pct: breakdown.Capability ?? 0, positive: true, formula: '% of vulnerable clients whose Intelliflo vulnerability record lists this category (a client can have more than one, so the four don’t need to sum to 100%).' },
                ...this.natureSignalMetrics,
            ];
        },
        // Rows built from evidence_signals. Each only appears once the Intelliflo
        // sync that produces it has run, so an older cache shows fewer rows rather
        // than zeros that would read as real findings.
        signals() {
            const s = this.parsedLiveMetrics?.evidence_signals;
            return s && s.total ? s : null;
        },
        natureSignalMetrics() {
            const s = this.signals;
            if (!s) return [];

            const rows = [];
            if (s.has_vulnerability) {
                rows.push({ label: 'Vulnerability status: Yes / Potential', value: `${s.has_vulnerability.Yes ?? 0} / ${s.has_vulnerability.Potential ?? 0}`, pct: null, formula: 'Intelliflo hasVulnerability on the current vulnerability records: Yes = confirmed, Potential = possible vulnerability not yet confirmed.' });
            }
            if (s.type_counts) {
                rows.push({ label: 'Temporary / Permanent', value: `${s.type_counts.Temporary ?? 0} / ${s.type_counts.Permanent ?? 0}`, pct: null, formula: 'Intelliflo vulnerability type on the current vulnerability records. Records with no type set are not counted.' });
            }
            if (s.age_bands && s.age_known) {
                Object.entries(s.age_bands)
                    .filter(([, b]) => b.clients > 0)
                    .forEach(([band, b]) => {
                        const pct = Math.round(b.vulnerable / b.clients * 100);
                        rows.push({ label: `Clients aged ${band} with a vulnerability`, value: `${b.vulnerable} of ${b.clients} (${pct}%)`, pct: null, formula: `Clients in this age band with a current Intelliflo vulnerability record ÷ all clients in the band. Age is from the date of birth in Intelliflo, on file for ${s.age_known.vulnerable} of ${s.total} vulnerable clients.` });
                    });
            }
            (s.advisers || [])
                .filter((a) => a.vulnerable > 0)
                .slice(0, 10)
                .forEach((a) => {
                    const pct = Math.round(a.vulnerable / a.clients * 100);
                    rows.push({ label: `Adviser: ${a.name || 'Unnamed'}`, value: `${a.vulnerable} of ${a.clients} clients (${pct}%)`, pct: null, formula: 'Clients of this adviser (Intelliflo current adviser) with a current vulnerability record ÷ all their clients. Advisers with the most clients are listed first; advisers with no vulnerable client recorded are not listed.' });
                });

            return rows;
        },
        identificationSectionMetrics() {
            const m = this.parsedLiveMetrics;
            if (!m) return [];

            const s = this.signals;
            const rows = [];
            if (s && s.identified_last_12_months !== undefined) {
                rows.push({ label: 'Vulnerabilities recorded in the last 12 months', value: `${s.identified_last_12_months} of ${s.total}`, pct: null, formula: 'Current Intelliflo vulnerability records whose created date is within the last 12 months.' });
            }
            rows.push({ label: 'Records with complete data', value: (m.completeness_rate ?? 0) + '%', pct: m.completeness_rate ?? 0, positive: true, formula: '% of vulnerability records with category, notes and a review date all populated ÷ vulnerable clients × 100.' });
            if (s && s.with_review_date !== undefined) {
                const missing = (key) => s.total - (s[key] ?? 0);
                rows.push({ label: 'Missing category / notes / review date', value: `${missing('with_categories')} / ${missing('with_notes')} / ${missing('with_review_date')} of ${s.total}`, pct: null, formula: 'Vulnerability records missing each detail that makes a record complete. One record can be missing more than one.' });
            }
            rows.push({ label: 'Records not reviewed in 24+ months', value: (m.stale_rate ?? 0) + '%', pct: m.stale_rate ?? 0, formula: '% of vulnerability records with no review date, or a review date more than 24 months ago.' });

            return rows;
        },
        supportSectionMetrics() {
            const m = this.parsedLiveMetrics;
            if (!m) return [];

            return [
                { label: 'Vulnerable clients with a linked service case', value: (m.support_coverage_rate ?? 0) + '%', pct: m.support_coverage_rate ?? 0, positive: true, formula: '% of vulnerable clients with at least one Intelliflo service case.' },
                { label: 'Open / unresolved service cases', value: String(m.open_service_cases), pct: null, formula: 'Count of service cases (for vulnerable clients) where Intelliflo’s isCompleted flag is false.' },
                ...this.supportSignalMetrics,
            ];
        },
        supportSignalMetrics() {
            const s = this.signals;
            if (!s) return [];

            // Only records that every vulnerable client should have get a RAG bar;
            // Power of Attorney or health notes are not expected for everyone.
            const row = (key, label, formula, bar = false) => (s[key] === undefined ? null : {
                label,
                value: `${s[key]} of ${s.total}`,
                pct: bar ? Math.round(s[key] / s.total * 100) : null,
                positive: true,
                formula,
            });
            const types = Object.keys(s.adjustment_types || {});

            return [
                row('with_adjustments', 'Needs & adjustments recorded', 'Vulnerability records with at least one adjustment made for the client in Intelliflo' + (types.length ? ' (' + types.slice(0, 8).join(', ') + ').' : '.'), true),
                row('with_action_taken', '“Action taken” recorded', 'Vulnerability records with the Intelliflo “action taken” notes filled in.', true),
                row('with_power_of_attorney', 'Power of Attorney recorded', 'Vulnerable clients with Power of Attorney granted on their Intelliflo client record.'),
                row('with_health_notes', 'Health or medical notes recorded', 'Vulnerable clients with health or medical-condition notes on their Intelliflo health profile.'),
                row('with_portal_assessed', 'Client-portal suitability assessed', 'Vulnerability records where Intelliflo’s “client portal suitable” has been answered.'),
            ].filter(Boolean);
        },
        // Shows the two raw per-client averages as plain counts, with no % and
        // no differential — the earlier version showed a confidence-weighted
        // % differential here and it read as contradicting Support's coverage
        // % (100% coverage next to a scary differential number) without the
        // weighting being understandable in-UI. The differential itself is
        // still calculated and still drives the Customer Outcome Score KPI
        // above (see outcomeScoreFormula) and the "Elevated service-case rate"
        // regulatory exception — only its display here is a plain count pair.
        outcomesSectionMetrics() {
            const m = this.parsedLiveMetrics;
            if (!m) return [];

            const rows = [];
            if (m.vulnerable_service_case_rate !== null && m.vulnerable_service_case_rate !== undefined) {
                rows.push({ label: 'Service cases per vulnerable client', value: String(m.vulnerable_service_case_rate), pct: null, formula: 'Total Intelliflo service cases for vulnerable clients ÷ vulnerable clients.' });
            }
            if (m.non_vulnerable_service_case_rate !== null && m.non_vulnerable_service_case_rate !== undefined) {
                rows.push({ label: 'Service cases per non-vulnerable client', value: String(m.non_vulnerable_service_case_rate), pct: null, formula: 'Total Intelliflo service cases for non-vulnerable clients ÷ non-vulnerable clients — shown for comparison only.' });
            }
            rows.push({ label: 'Complaints data from CRM', value: 'Not available (Intelliflo has no complaints resource)', pct: null });

            return rows;
        },
        parsedQuestionVerdicts() {
            if (!this.questionVerdicts) return null;
            try {
                return JSON.parse(this.questionVerdicts) || null;
            } catch (e) {
                return null;
            }
        },
        // Per-question AI verdicts have their own state, independent of the
        // CRM-metric sections: they additionally need the AI sync step to
        // have completed, not just the CRM sync.
        verdictState() {
            if (!this.ioConnected) return 'not_connected';
            if (!this.parsedLiveMetrics) return 'pending_sync';
            if (!this.parsedQuestionVerdicts || this.parsedQuestionVerdicts.length === 0) return 'pending_verdicts';
            return 'synced';
        },
        verdictStatusMessage() {
            if (this.verdictState === 'not_connected' || this.verdictState === 'pending_sync') {
                return this.syncStatusMessage;
            }
            if (this.verdictState === 'pending_verdicts') {
                return 'CRM data has synced, but the AI question-by-question assessment hasn’t run yet.';
            }
            return '';
        },
        controlEffectivenessSection() {
            return {
                key: 'control',
                title: SECTION_TITLES.control,
                info: SECTION_INFO.control,
                live: this.verdictState === 'synced',
                type: 'verdicts',
                metrics: [],
                statusMessage: this.verdictStatusMessage,
                questions: this.verdictState === 'synced' ? this.parsedQuestionVerdicts : [],
            };
        },
        // Real client names/notes pulled straight from Intelliflo (Vulnerabilities
        // notes/action-taken, legacy ServiceCase vulnerability-owner notes, Client
        // health/medical notes) — shown to this account's own authorised staff,
        // who already have access to these clients in Intelliflo itself. Only the
        // GPT-bound copy of this same data is pseudonymised (Client A/B/C); this
        // is not that copy.
        crmNotesSection() {
            return {
                key: 'crmNotes',
                title: 'Client Notes (CRM)',
                live: this.hasCrmNotes,
                type: 'notes',
                metrics: [],
                statusMessage: this.syncStatusMessage || 'CRM data has synced, but no free-text notes were found on the sampled clients.',
                notes: this.hasCrmNotes ? this.parsedCrmNotes : [],
            };
        },
        sections() {
            const live = this.syncState === 'synced';
            const outcomesLive = live && this.parsedLiveMetrics?.vulnerable_service_case_rate !== null
                && this.parsedLiveMetrics?.vulnerable_service_case_rate !== undefined;

            return [
                { key: 'nature', title: SECTION_TITLES.nature, info: SECTION_INFO.nature, live, metrics: this.natureSectionMetrics },
                { key: 'identification', title: SECTION_TITLES.identification, info: SECTION_INFO.identification, live, metrics: this.identificationSectionMetrics },
                { key: 'support', title: SECTION_TITLES.support, info: SECTION_INFO.support, live, metrics: this.supportSectionMetrics },
                { key: 'outcomes', title: SECTION_TITLES.outcomes, info: SECTION_INFO.outcomes, live: outcomesLive, metrics: this.outcomesSectionMetrics },
                this.controlEffectivenessSection,
                this.crmNotesSection,
            ];
        },
    },
    methods: {
        toggle(key) {
            this.openSections[key] = !this.openSections[key];
        },
        evidenceChips(q) {
            const e = q.evidence || {};
            const chips = [];
            const docs = e.action_tracker_documents || [];

            if (docs.length) {
                chips.push({ label: 'Action Tracker file' + (docs.length > 1 ? ' ×' + docs.length : ''), title: docs.join(', ') });
            }
            if (e.action_tracker_notes) {
                chips.push({ label: 'Action Tracker note' + (e.action_tracker_notes > 1 ? ' ×' + e.action_tracker_notes : ''), title: 'Notes the firm added to this question in Action Tracker' });
            }
            if (e.monitor_document) {
                const d = e.monitor_document;
                chips.push({
                    label: 'MO®nitor scan ' + d.score + '%',
                    sub: d.name + ' · scanned in MO®nitor ' + d.scanned_at,
                    note: 'Files added in Action Tracker are not scanned.',
                    href: d.url,
                    title: 'Latest MO®nitor AI scan of this policy document — click to open the audit result. This is the MO®nitor upload, separate from files added in Action Tracker.',
                });
            }
            if (e.crm) {
                chips.push({ label: 'Intelliflo CRM data', expandable: true, title: 'Click to see what Intelliflo holds for this question' });
            }

            return chips;
        },
        ratingLines(q) {
            const r = q.ratings || {};
            const line = (label, item, title, naTitle) => {
                if (!item || item.score === null || item.score === undefined || !item.rag) {
                    return { label, text: 'n/a', cls: 'rag-grey', title: naTitle };
                }

                return { label, text: item.rag, cls: 'rag-' + item.rag.toLowerCase(), title: title(item.score) };
            };

            return [
                line('Control', r.control, (n) => 'Control ' + n + '%: how strongly the firm declared this is in place — "' + q.firm_answer + '"', 'No declared answer to score'),
                line('Evidence', r.evidence, (n) => 'Evidence ' + n + '%: the AI verdict (' + this.verdictLabel(q.verdict) + ') on the evidence found', 'Not assessed'),
                line('Outcome', r.outcome && r.outcome.applies ? r.outcome : null,
                    (n) => 'Outcome ' + n + '%: the business-function customer outcome score (service cases of vulnerable vs other clients)',
                    r.outcome && r.outcome.applies ? 'No service-case data to score outcomes yet' : 'Outcomes are not assessed for this question'),
            ];
        },
        toggleCrm(q) {
            this.openCrm = { ...this.openCrm, [q.question_id]: !this.openCrm[q.question_id] };
        },
        showClientNotes() {
            this.openSections.crmNotes = true;
            this.$nextTick(() => {
                const el = this.$el.querySelector('.notes-table');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },
        refreshEvidence(mode) {
            this.refreshState = 'sending';
            axios.post(this.refreshUrl, { mode })
                .then((response) => {
                    this.refreshState = 'idle';
                    this.progress = response.data || null;
                    this.watchedRefresh = true;
                    this.startPolling();
                })
                .catch(() => { this.refreshState = 'error'; });
        },
        startPolling() {
            this.stopPolling();
            if (!this.statusUrl || !this.refreshRunning) return;

            this.clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
            this.pollTimer = setInterval(() => {
                axios.get(this.statusUrl)
                    .then((response) => {
                        if (response.data) this.progress = response.data;
                        if (!this.refreshRunning) this.stopPolling();
                    })
                    .catch(() => {});
            }, 3000);
        },
        stopPolling() {
            clearInterval(this.pollTimer);
            clearInterval(this.clockTimer);
            this.pollTimer = null;
            this.clockTimer = null;
            this.now = Date.now();
        },
        reloadPage() {
            window.location.reload();
        },
        barStyle(metric) {
            let color;

            if (metric.positive) {
                // High = good (e.g. category identification, record completeness,
                // service-case coverage): 0-30% Red, 31-70% Amber, 70%+ Green.
                if (metric.pct < 30) color = '#DE301D';
                else if (metric.pct <= 70) color = '#F1C40F';
                else color = '#2ECC71';
            } else {
                // High = bad (e.g. stale-record rate): unchanged from before.
                color = '#2ECC71';
                if (metric.pct >= 60) color = '#DE301D';
                else if (metric.pct >= 30) color = '#F1C40F';
            }

            return { width: Math.min(metric.pct, 100) + '%', backgroundColor: color };
        },
        verdictBadgeClass(verdict) {
            const map = {
                VALIDATED: 'badge-validated',
                CONTRADICTED: 'badge-contradicted',
                INSUFFICIENT_EVIDENCE: 'badge-insufficient',
                EMERGING_RISK: 'badge-emerging',
            };
            return map[verdict] || 'badge-insufficient';
        },
        verdictLabel(verdict) {
            return String(verdict || '').replace(/_/g, ' ');
        },
        ragColor(pct) {
            if (pct === null || pct === undefined) return '#999';
            if (pct < 34) return '#DE301D';
            if (pct < 68) return '#F1C40F';
            return '#2ECC71';
        },
        ragLabel(pct) {
            if (pct === null || pct === undefined) return 'Not assessed';
            if (pct < 34) return 'Red';
            if (pct < 68) return 'Amber';
            return 'Green';
        },
    },
};
</script>

<style scoped>
.vulnerability-evidence {
    padding-bottom: 20px;
}

.beta-pill {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #F1C40F;
    color: #1a1a1a;
    font-size: 11px;
    font-weight: 600;
    vertical-align: middle;
}

.meta {
    color: #888;
    font-size: 13px;
}

.mock-banner {
    background: #FEF9E7;
    border: 1px solid #F1C40F;
    border-radius: 4px;
    padding: 10px 14px;
    margin: 14px 0;
    font-size: 13px;
    color: #7d6608;
}

.mock-banner i {
    margin-right: 6px;
}

.illustrative-tag {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 8px;
    background: #eee;
    color: #777;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    vertical-align: middle;
}

.info-icon {
    position: relative;
    display: inline-block;
    margin-left: 4px;
    color: #999;
    font-size: 12px;
    cursor: help;
    vertical-align: middle;
    outline: none;
}

.info-icon:hover,
.info-icon:focus {
    color: #555;
}

.info-tooltip {
    display: none;
    position: absolute;
    z-index: 50;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 6px;
    width: 240px;
    padding: 8px 10px;
    border-radius: 6px;
    background: #333;
    color: #fff;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.4;
    text-align: left;
    text-transform: none;
    letter-spacing: normal;
    white-space: normal;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.info-icon:hover .info-tooltip,
.info-icon:focus .info-tooltip {
    display: block;
}

.section-status-message {
    margin: 0;
    padding: 10px 6px;
    font-size: 13px;
    color: #777;
    font-style: italic;
}

.kpi-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin: 14px 0;
}

@media (max-width: 900px) {
    .kpi-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.kpi-card {
    box-sizing: border-box;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    padding: 16px 8px;
    text-align: center;
}

.kpi-value {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
}

.kpi-label {
    margin-top: 4px;
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.traffic-row {
    display: flex;
    gap: 24px;
    background: #fafafa;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 18px;
}

.traffic-item {
    display: flex;
    align-items: center;
    gap: 10px;
}

.traffic-item .dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    flex-shrink: 0;
}

.traffic-value {
    font-weight: 700;
    font-size: 14px;
}

.traffic-label {
    font-size: 12px;
    color: #666;
}

.ai-narrative {
    background: #F4F1FB;
    border: 1px solid #D6C9F0;
    border-radius: 6px;
    padding: 16px 18px;
    margin-bottom: 18px;
}

.ai-header {
    font-weight: 700;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.ai-header i {
    color: #8E44AD;
}

.ai-logo {
    width: 28px;
    height: auto;
    flex: none;
}

.classification-badge {
    margin-left: auto;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #fff;
}

.badge-validated { background: #2ECC71; }
.badge-contradicted { background: #DE301D; }
.badge-insufficient { background: #999; }
.badge-emerging { background: #E67E22; }

.ai-narrative p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
}

.verdict-ratings {
    min-width: 96px;
    white-space: nowrap;
}

.rating-line {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 1px 0;
    font-size: 12px;
}

.rag-dot {
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #bbb;
}

.rag-dot.rag-red { background: #DE301D; }
.rag-dot.rag-amber { background: #F1C40F; }
.rag-dot.rag-green { background: #2ECC71; }
.rag-dot.rag-grey { background: #d6d6d6; }

.rating-label {
    color: #555;
}

.rating-value {
    margin-left: auto;
    font-weight: 600;
    color: #333;
}

.rag-pill {
    margin-left: auto;
    padding: 3px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #fff;
    background: #999;
}

.rag-red { background: #DE301D; }
.rag-amber { background: #F1C40F; color: #1a1a1a; }
.rag-green { background: #2ECC71; }

.ai-headline {
    margin-bottom: 4px;
    font-size: 15px;
    font-weight: 700;
}

.ai-narrative .ai-priority {
    margin-top: 8px;
}

.ai-priority ol {
    margin: 4px 0 0;
    padding-left: 20px;
}

.ai-section {
    margin-top: 8px;
}

.ai-section-title {
    font-size: 13px;
    font-weight: 700;
}

.ai-section p {
    margin: 2px 0 0;
}

.ai-prompt {
    margin-top: 10px;
    font-size: 12px;
}

.ai-prompt i {
    margin-right: 4px;
    font-size: 10px;
}

.ai-prompt-text {
    max-height: 360px;
    margin-top: 6px;
    padding: 10px;
    overflow: auto;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    background: #fff;
    font-size: 11px;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
}

.section-info .info-tooltip {
    left: 0;
    transform: none;
    width: 320px;
}

.evidence-chip-note {
    font-style: italic;
}

.ai-footnote {
    margin-top: 10px;
    font-size: 11px;
    color: #888;
}

.drilldown {
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    margin-bottom: 18px;
}

.drilldown-section:first-child .section-header {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.drilldown-section + .drilldown-section {
    border-top: 1px solid #e5e5e5;
}

.section-header {
    padding: 12px 16px;
    background: #fafafa;
    cursor: pointer;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-header.static {
    cursor: default;
}

.section-header i {
    width: 12px;
}

.section-body {
    padding: 4px 16px 12px;
}

.metrics-table {
    margin: 0;
}

.metrics-table td {
    border-top: 1px solid #f0f0f0;
    padding: 8px 6px;
    vertical-align: middle;
    font-size: 13px;
}

.metric-label {
    color: #444;
}

.metric-value {
    font-weight: 600;
    white-space: nowrap;
}

.metric-bar {
    width: 140px;
}

.verdicts-table {
    margin: 0;
}

.verdicts-table th {
    font-size: 11px;
    text-transform: uppercase;
    color: #888;
    border-top: none;
    text-align: left;
    padding: 6px;
}

.verdicts-table td {
    border-top: 1px solid #f0f0f0;
    padding: 10px 6px;
    vertical-align: top;
    font-size: 13px;
}

.verdict-question {
    color: #333;
    font-weight: 600;
    max-width: 260px;
}

.verdict-answer {
    color: #555;
    max-width: 200px;
}

.verdict-rationale {
    color: #666;
    max-width: 320px;
}

.verdicts-table .classification-badge {
    margin-left: 0;
    display: inline-block;
}

.verdicts-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 10px 6px;
    font-size: 13px;
}

.assessed-at {
    color: #888;
}

.refresh-btn {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    padding: 4px 12px;
    cursor: pointer;
    font-size: 13px;
}

.refresh-btn-primary {
    background: #F1C40F;
    border-color: #D4AC0D;
    font-weight: 600;
}

.changes-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #F1C40F;
    border-radius: 4px;
    background: #FEF9E7;
    color: #7d6608;
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.refresh-message {
    color: #555;
}

.refresh-progress {
    margin: 0 0 14px;
    padding: 12px 14px;
    border: 1px solid #D6C9F0;
    border-radius: 6px;
    background: #F4F1FB;
    font-size: 13px;
}

.refresh-progress-done {
    border-color: #A9DFBF;
    background: #EAFAF1;
}

.refresh-progress-warning {
    border-color: #F1C40F;
    background: #FEF9E7;
}

.refresh-progress-failed {
    border-color: #F5B7B1;
    background: #FDEDEC;
}

.refresh-progress-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.refresh-progress-head i {
    margin-right: 4px;
}

.refresh-progress-time {
    margin-left: auto;
    color: #888;
    font-size: 12px;
}

.refresh-steps {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
}

.refresh-steps li {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #999;
}

.refresh-steps li.step-done {
    color: #1E8449;
}

.refresh-steps li.step-current {
    color: #333;
    font-weight: 600;
}

.refresh-steps li.step-failed {
    color: #DE301D;
    font-weight: 600;
}

.step-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 1px solid currentColor;
    border-radius: 50%;
    font-size: 10px;
}

.refresh-progress-message {
    margin: 8px 0 0;
}

.refresh-error {
    color: #DE301D;
}

.verdict-evidence {
    max-width: 220px;
    overflow-wrap: anywhere;
}

.evidence-chip {
    display: inline-block;
    margin: 0 4px 4px 0;
    padding: 2px 8px;
    border-radius: 10px;
    background: #E8F8EF;
    color: #1E7B45;
    font-size: 11px;
    font-weight: 600;
}

.evidence-chip {
    max-width: 100%;
    box-sizing: border-box;
    word-break: break-word;
}

.evidence-chip-link {
    text-decoration: none;
}

.evidence-chip-link:hover {
    background: #D4F1E0;
}

.evidence-chip-toggle {
    cursor: pointer;
    user-select: none;
}

.evidence-chip-toggle:hover {
    background: #D4F1E0;
}

.crm-detail {
    box-sizing: border-box;
    max-width: 100%;
    margin: 2px 0 6px;
    padding: 8px 10px;
    border: 1px solid #CFE9DA;
    border-radius: 6px;
    background: #F6FCF9;
    font-size: 12px;
    overflow-wrap: anywhere;
}

.crm-detail-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 2px 10px;
    padding: 3px 0;
    border-bottom: 1px solid #E6F3EC;
}

.crm-detail-row:last-of-type {
    border-bottom: none;
}

.crm-detail-label {
    flex: 1 1 120px;
    min-width: 0;
    color: #555;
}

.crm-detail-value {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    font-weight: 600;
    text-align: right;
    color: #1E7B45;
}

.crm-detail-link {
    display: inline-block;
    margin-top: 4px;
}

.evidence-chip-sub {
    display: block;
    margin-top: 1px;
    font-size: 10px;
    font-weight: 400;
    color: #3F7F5A;
}

.evidence-none {
    display: block;
    color: #999;
    font-size: 12px;
    font-style: italic;
    margin-bottom: 4px;
}

.evidence-link {
    display: block;
    font-size: 12px;
}

.notes-table {
    margin: 0;
}

.notes-table th {
    font-size: 11px;
    text-transform: uppercase;
    color: #888;
    border-top: none;
    text-align: left;
    padding: 6px;
}

.notes-table td {
    border-top: 1px solid #f0f0f0;
    padding: 10px 6px;
    vertical-align: top;
    font-size: 13px;
}

.note-client {
    color: #333;
    font-weight: 600;
    max-width: 160px;
    white-space: nowrap;
}

.note-source {
    color: #888;
    font-size: 12px;
    max-width: 220px;
}

.note-text {
    color: #444;
    max-width: 380px;
}

.bar-track {
    background: #eee;
    border-radius: 4px;
    height: 6px;
    overflow: hidden;
}

.bar-fill {
    height: 100%;
    border-radius: 4px;
}

.exceptions-panel {
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 14px;
}

.exceptions-table {
    margin: 0;
}

.exceptions-table th {
    font-size: 12px;
    text-transform: uppercase;
    color: #888;
    border-top: none;
}

.exceptions-table td {
    font-size: 13px;
    vertical-align: middle;
}

.severity-badge {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
}

.sev-high { background: #DE301D; }
.sev-medium { background: #F1C40F; color: #1a1a1a; }
.sev-low { background: #999; }

.confidence-footnote {
    font-size: 12px;
    color: #888;
    font-style: italic;
}
</style>
